import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { isProtected } from '../../utils/protection';
import { ProtectedBadge } from '../ProtectedBadge';
import { DossierFlavorChip } from './DossierFlavorChip';
import { raceChip, rankChip, personalityChip } from '../../utils/dossierFlavor';
import {
  MobileDossierCbCard,
  MobileDossierSomCard,
  MobileDossierSurveyCard,
  MobileDossierInfiltrateCard,
} from './MobileDossierIntelCards';
import type { DossierProvinceRow } from '../../types/dossier';
import type { CrystalBallData } from '../../types/intel';

// This view's own sticky action bar's rendered height, folded into the content padding-bottom
// reserve (CLAUDE.md SF-132 pattern — an offset alone doesn't reserve scroll space).
const ACTION_BAR_HEIGHT_PX = 64;
// MobileTabBar's own reserved bottom space on every sibling route — matches
// MobileTargetDetailView.tsx's exact reserve, gated on `showSiblingChrome` (Phase 3: this
// component's top block carries NO analogous offset — no collision exists there at rest; see
// Application Interface's Sibling-chrome / floating-button interaction section).
const TAB_BAR_OFFSET_PX = 96;

interface MobileDossierDetailViewProps {
  member: DossierProvinceRow;
  currentTick: number;
  onBack: () => void;
  showSiblingChrome: boolean;
}

/**
 * SF-156 Component 5: in-page replacement for the roster when `?member=` is set — mirrors
 * `MobileTargetDetailView.tsx`'s `?target=` mechanism (real URL state, not local component state,
 * so hardware Back and refresh both work). Renders four stacked read-only intel cards + a sticky
 * action bar (War Room / Thievery / Magic).
 */
export function MobileDossierDetailView({ member, currentTick, onBack, showSiblingChrome }: MobileDossierDetailViewProps) {
  const navigate = useNavigate();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const protectedNow = isProtected(member.protectionExpiresAtTick, currentTick);
  const cb = (member.latestCb?.data as CrystalBallData | undefined) ?? null;

  // Mount/member-change focus — the mechanism the shell-level focus-restore (MobileDossierShell)
  // hands off to on the way back in, mirroring MobileTargetDetailView.tsx's own precedent.
  useEffect(() => {
    h1Ref.current?.focus();
  }, [member.provinceId]);

  const bottomOffset = showSiblingChrome ? TAB_BAR_OFFSET_PX : 0;
  const paddingBottomReserve = (showSiblingChrome ? TAB_BAR_OFFSET_PX : 0) + ACTION_BAR_HEIGHT_PX + 16;

  const queryStr = `?targetId=${member.provinceId}&targetName=${encodeURIComponent(member.provinceName)}&targetLoc=${encodeURIComponent(member.location)}`;

  return (
    <div data-testid="dossier-detail-root" style={{ paddingBottom: paddingBottomReserve }}>
      <div style={{ padding: 12 }}>
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to roster"
          style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: COLORS.link, fontSize: 14, cursor: 'pointer', padding: '4px 0' }}
        >
          ← Back
        </button>

        <h1 ref={h1Ref} tabIndex={-1} style={{ fontSize: 19, fontWeight: 700, color: COLORS.text.primary, margin: '6px 0 2px', outline: 'none' }}>
          {member.provinceName}
          <span style={{ color: COLORS.link, fontSize: 12, fontWeight: 500, marginLeft: 6 }}>({member.location})</span>
          {member.isMonarch && <span style={{ color: COLORS.negative, fontSize: 11, fontWeight: 700, marginLeft: 8 }}>♛ Monarch</span>}
        </h1>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 12, color: COLORS.text.secondary, marginTop: 8 }}>
          {protectedNow && <ProtectedBadge protectionExpiresAtTick={member.protectionExpiresAtTick} currentTick={currentTick} />}
          <DossierFlavorChip meta={raceChip(member.race)} />
          <DossierFlavorChip meta={rankChip(member.rank)} />
          {cb && <DossierFlavorChip meta={personalityChip(cb.personality)} />}
          <span>Land (acres) {formatNumber(member.land)}</span>
          <span style={{ color: COLORS.gold }}>NW (gc) {formatNumber(member.networth)}</span>
        </div>
      </div>

      <div style={{ padding: '0 12px' }}>
        <MobileDossierCbCard entry={member.latestCb} currentTick={currentTick} onGather={() => navigate(`/thievery${queryStr}`)} />
        <MobileDossierSomCard entry={member.latestSom} currentTick={currentTick} onGather={() => navigate(`/thievery${queryStr}`)} />
        <MobileDossierSurveyCard entry={member.latestSurvey} currentTick={currentTick} onGather={() => navigate(`/thievery${queryStr}`)} />
        <MobileDossierInfiltrateCard entry={member.latestInfiltrate} currentTick={currentTick} onGather={() => navigate(`/thievery${queryStr}`)} />
      </div>

      <div
        data-testid="dossier-detail-actionbar"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: bottomOffset,
          zIndex: 30,
          padding: '8px 12px',
          display: 'flex',
          gap: 8,
          background: COLORS.bg,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(`/attack${queryStr}`)}
          style={{ flex: 2, minHeight: 44, borderRadius: 6, border: 'none', background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          War Room
        </button>
        <button
          type="button"
          onClick={() => navigate(`/thievery${queryStr}`)}
          style={{ flex: 1, minHeight: 44, borderRadius: 6, background: 'transparent', color: '#c4b0e8', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(107,79,160,0.4)' }}
        >
          Thievery
        </button>
        <button
          type="button"
          onClick={() => navigate(`/mystics${queryStr}`)}
          style={{ flex: 1, minHeight: 44, borderRadius: 6, background: 'transparent', color: COLORS.link, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(107,144,224,0.3)' }}
        >
          Magic
        </button>
      </div>
    </div>
  );
}
